import * as Icons from '@components/Icons';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Singular en minúscula — "credencial", "función"… Se usa en aria-labels
   *  y en el botón "+ Añadir X". */
  singular: string;
  placeholder?: string;
  /** Texto cuando no hay entradas. Default: "Sin entradas aún.". */
  emptyLabel?: string;
  maxEntries?: number;
  maxLengthPerEntry?: number;
}

const DEFAULT_MAX = 10;
const DEFAULT_LEN = 200;

/**
 * Editor genérico de lista de strings: un input por entrada con botón
 * eliminar y un botón "+ Añadir <singular>" al final. Cada entrada sigue
 * siendo un string en el array; este componente solo cambia la UI.
 *
 * Usado para `users.credentials` (singular="credencial") y
 * `users.functions` (singular="función"). Sin estado interno: el caller
 * mantiene el array y nosotros le devolvemos mutaciones.
 */
export function StringListEditor({
  value,
  onChange,
  singular,
  placeholder,
  emptyLabel,
  maxEntries = DEFAULT_MAX,
  maxLengthPerEntry = DEFAULT_LEN,
}: Props) {
  function update(index: number, text: string) {
    const next = [...value];
    next[index] = text;
    onChange(next);
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function add() {
    if (value.length >= maxEntries) return;
    onChange([...value, '']);
  }

  const atLimit = value.length >= maxEntries;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {value.length === 0 && (
        <span style={{ fontSize: 12, color: 'var(--fg-soft, #6b7280)' }}>
          {emptyLabel ?? 'Sin entradas aún.'}
        </span>
      )}
      {value.map((entry, i) => (
        <div
          key={i}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <input
            className="input"
            type="text"
            maxLength={maxLengthPerEntry}
            value={entry}
            onChange={(e) => { update(i, e.target.value); }}
            placeholder={placeholder}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="icon-btn"
            data-variant="ghost"
            aria-label={`Eliminar ${singular}`}
            onClick={() => { remove(i); }}
          >
            <Icons.X size={14} />
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          className="btn"
          onClick={add}
          disabled={atLimit}
          aria-label={`Añadir ${singular}`}
        >
          + Añadir {singular}
        </button>
        <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--fg-soft, #6b7280)' }}>
          {atLimit
            ? `Máximo ${String(maxEntries)} entradas.`
            : `${String(value.length)}/${String(maxEntries)} · ${String(maxLengthPerEntry)} caracteres por entrada.`}
        </span>
      </div>
    </div>
  );
}
